import React, { useState, useCallback, useMemo, Suspense } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PageTransition } from '@/components/PageTransition';
import { AccessDenied } from '@/components/AccessDenied';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableCard } from '@/components/application/table/table-fixed';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanyTeamData } from '@/hooks/useCompanyTeamData';
import { toast } from 'sonner';
import {
  Users,
  Search,
  Filter,
  Download,
  MoreHorizontal,
  MapPin,
  Phone,
  Building,
  UserPlus,
  Eye,
  Edit,
  Shield,
  Send,
  RefreshCw,
  XCircle,
  Trash2,
  UserCheck,
  UserX,
  X,
  Loader2,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { GLOBAL_TOOLS } from '@/config/tools';

// Lazy load InviteTeamMemberDialog
const InviteTeamMemberDialog = lazyWithRetry(() =>
  import('./InviteTeamMemberDialog').then(module => ({ default: module.InviteTeamMemberDialog }))
);

// Helper function - extracted to module level
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Helper function - extracted to module level
const getPermissionBadgeColor = (permission) => {
  const normalizedPermission = permission?.toLowerCase();
  switch (normalizedPermission) {
    case 'admin':
      return 'default';
    case 'manager':
      return 'secondary';
    default:
      return 'outline';
  }
};

// Generate skeleton placeholder items for loading state
const skeletonItems = Array.from({ length: 5 }, (_, i) => ({
  id: `skeleton-${i}`,
  name: '',
  email: '',
  role: '',
  department: '',
  spaces: [],
  location: '',
  phone: '',
  permissions: '',
  isLoading: true,
}));

export function CompanyTeamPage() {
  const { userProfile, company, can, loading: authLoading, permissionsLoaded } = useAuth();
  const navigate = useNavigate();
  usePageTitle('Company Team');

  // Filter and pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpace, setFilterSpace] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAccountStatus, setFilterAccountStatus] = useState('all');
  const [filterArchiveStatus, setFilterArchiveStatus] = useState('active');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => getStoredPageSize(10));

  // Multi-select state (using Set for React Aria compatibility)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [memberToArchive, setMemberToArchive] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [memberToRestore, setMemberToRestore] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);

  // Check permissions
  const canManageUsers = can('users.manage');

  // Use optimized hook for data fetching
  const {
    members,
    uniqueSpaceNames,
    stats,
    pagination,
    loading,
    refetch
  } = useCompanyTeamData(company?.id, {
    search: searchQuery,
    status: filterStatus,
    accountStatus: filterAccountStatus,
    archiveStatus: filterArchiveStatus,
    spaceName: filterSpace,
    sortField,
    sortDirection,
    page: currentPage,
    limit: itemsPerPage,
    enabled: permissionsLoaded && canManageUsers && !!company?.id
  });

  // Clear selection when filters change or data reloads
  React.useEffect(() => {
    setSelectedIds(new Set());
  }, [searchQuery, filterStatus, filterAccountStatus, filterArchiveStatus, filterSpace, currentPage]);

  // Count of selected items
  const selectedCount = selectedIds.size;

  // Global tools for company-wide navigation (from central config)
  const globalTools = GLOBAL_TOOLS;

  // Reset to page 1 when filters change
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleFilterSpaceChange = useCallback((space) => {
    setFilterSpace(space);
    setCurrentPage(1);
  }, []);

  const handleFilterStatusChange = useCallback((status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  }, []);

  const handleFilterAccountStatusChange = useCallback((status) => {
    setFilterAccountStatus(status);
    setCurrentPage(1);
  }, []);

  const handleFilterArchiveStatusChange = useCallback((value) => {
    setFilterArchiveStatus(value);
    setCurrentPage(1);
  }, []);

  const handleItemsPerPageChange = useCallback((value) => {
    const val = Number(value);
    setItemsPerPage(val);
    setCurrentPage(1);
    setStoredPageSize(val);
  }, []);

  const handleViewProfile = useCallback((userId) => {
    navigate(`/profile/${userId}`);
  }, [navigate]);

  const handleEditMember = useCallback((member) => {
    navigate(`/company-team/edit/${member.userId || member.id}`);
  }, [navigate]);

  const handleExportMembers = useCallback(() => {
    if (!members || members.length === 0) {
      toast.error('No members to export');
      return;
    }

    const headers = ['Name', 'Email', 'Job Title', 'Role', 'Spaces', 'Phone', 'Status'];
    const rows = members.map(member => [
      member.name || '',
      member.email || '',
      member.role || '',
      member.permissions || 'User',
      member.spaces?.map(s => s.name).join('; ') || '',
      member.phone || '',
      member.isPending ? 'Pending' : member.status || 'Active'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        const value = String(cell);
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${company?.name || 'company'}_team_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${members.length} members`);
  }, [members, company?.name]);

  const handleResendInvitation = useCallback(async (member) => {
    if (!member.invitationId) {
      toast.error('No invitation found to resend');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: member.email,
          companyId: company.id,
          isResend: true,
          invitationId: member.invitationId
        }
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success(data?.emailSent ? 'Invitation resent successfully' : 'Invitation updated (email delivery pending)');
      refetch();
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('Failed to resend invitation');
    }
  }, [company?.id, refetch]);

  const handleSendInvitation = useCallback(async (member) => {
    if (!member.userId) {
      toast.error('User profile not found');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: member.email,
          role: member.permissions || 'User',
          companyId: company.id,
          existingUserProfileId: member.userId
        }
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error.includes('already pending') ? 'An invitation has already been sent to this email' : data.error);
        return;
      }

      toast.success(data?.emailSent ? 'Invitation sent successfully' : 'Invitation created (email delivery pending)');
      refetch();
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    }
  }, [company?.id, refetch]);

  const handleCancelInvitation = useCallback(async (member) => {
    if (!member.invitationId) {
      toast.error('No invitation found to cancel');
      return;
    }

    try {
      const { error } = await supabase
        .from('company_invitations')
        .update({ status: 'cancelled' })
        .eq('id', member.invitationId)
        .eq('company_id', company.id);

      if (error) throw error;

      toast.success('Invitation cancelled');
      refetch();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  }, [company?.id, refetch]);

  const handleDeleteMember = useCallback((member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteMember = useCallback(async () => {
    if (!memberToDelete?.userId) {
      toast.error('Unable to remove member');
      return;
    }

    try {
      const { error } = await supabase
        .from('company_members')
        .delete()
        .eq('company_id', company.id)
        .eq('user_id', memberToDelete.userId);

      if (error) throw error;

      toast.success(`${memberToDelete.name} has been removed from the company`);
      refetch();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member from company');
    } finally {
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
    }
  }, [memberToDelete, company?.id, refetch]);

  // Get selected members data (must be before handlers that reference it)
  const selectedMembers = useMemo(() => {
    return members.filter(m => selectedIds.has(m.id));
  }, [members, selectedIds]);

  const handleArchiveMember = useCallback((member) => {
    setMemberToArchive(member);
    setArchiveDialogOpen(true);
  }, []);

  const confirmArchiveMember = useCallback(async () => {
    if (!memberToArchive?.userId) return;

    setArchiveLoading(true);
    try {
      const { data, error } = await supabase.rpc('archive_company_member', {
        p_company_id: company.id,
        p_user_id: memberToArchive.userId
      });

      if (error) throw error;
      if (!data?.success) {
        toast.error(data?.error || 'Failed to archive member');
        return;
      }

      const spacesMsg = data.spaces_removed > 0
        ? ` and removed from ${data.spaces_removed} space${data.spaces_removed !== 1 ? 's' : ''}`
        : '';
      toast.success(`${memberToArchive.name} has been archived${spacesMsg}`);
      refetch();
    } catch (error) {
      console.error('Error archiving member:', error);
      toast.error('Failed to archive member');
    } finally {
      setArchiveLoading(false);
      setArchiveDialogOpen(false);
      setMemberToArchive(null);
    }
  }, [memberToArchive, company?.id, refetch]);

  const handleRestoreMember = useCallback((member) => {
    setMemberToRestore(member);
    setRestoreDialogOpen(true);
  }, []);

  const confirmRestoreMember = useCallback(async () => {
    if (!memberToRestore?.userId) return;

    setRestoreLoading(true);
    try {
      const { data, error } = await supabase.rpc('restore_company_member', {
        p_company_id: company.id,
        p_user_id: memberToRestore.userId
      });

      if (error) throw error;
      if (!data?.success) {
        toast.error(data?.error || 'Failed to restore member');
        return;
      }

      toast.success(`${memberToRestore.name} has been restored`);
      if (filterArchiveStatus === 'archived') setFilterArchiveStatus('active');
      refetch();
    } catch (error) {
      console.error('Error restoring member:', error);
      toast.error('Failed to restore member');
    } finally {
      setRestoreLoading(false);
      setRestoreDialogOpen(false);
      setMemberToRestore(null);
    }
  }, [memberToRestore, company?.id, refetch]);

  const handleBulkArchive = useCallback(async () => {
    const archivable = selectedMembers.filter(m => !m.isArchived && !m.isPending && m.userId && m.userId !== userProfile?.id);
    if (archivable.length === 0) return;

    setBulkActionLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const member of archivable) {
      try {
        const { data, error } = await supabase.rpc('archive_company_member', {
          p_company_id: company.id,
          p_user_id: member.userId
        });
        if (error || !data?.success) { errorCount++; continue; }
        successCount++;
      } catch { errorCount++; }
    }

    if (successCount > 0) toast.success(`Archived ${successCount} member${successCount !== 1 ? 's' : ''}`);
    if (errorCount > 0) toast.error(`Failed to archive ${errorCount} member${errorCount !== 1 ? 's' : ''}`);

    setSelectedIds(new Set());
    setBulkArchiveDialogOpen(false);
    setBulkActionLoading(false);
    refetch();
  }, [selectedMembers, company?.id, userProfile?.id, refetch]);

  const handleBulkRestore = useCallback(async () => {
    const restorable = selectedMembers.filter(m => m.isArchived && m.userId);
    if (restorable.length === 0) return;

    setBulkActionLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const member of restorable) {
      try {
        const { data, error } = await supabase.rpc('restore_company_member', {
          p_company_id: company.id,
          p_user_id: member.userId
        });
        if (error || !data?.success) { errorCount++; continue; }
        successCount++;
      } catch { errorCount++; }
    }

    if (successCount > 0) toast.success(`Restored ${successCount} member${successCount !== 1 ? 's' : ''}`);
    if (errorCount > 0) toast.error(`Failed to restore ${errorCount} member${errorCount !== 1 ? 's' : ''}`);

    setSelectedIds(new Set());
    if (filterArchiveStatus === 'archived') setFilterArchiveStatus('active');
    setBulkActionLoading(false);
    refetch();
  }, [selectedMembers, company?.id, filterArchiveStatus, refetch]);

  const handleOpenInviteDialog = useCallback(() => {
    setInviteDialogOpen(true);
  }, []);

  // Multi-select handlers (now managed by React Aria)
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Bulk actions
  const handleBulkRemove = useCallback(async () => {
    if (selectedMembers.length === 0) return;

    setBulkActionLoading(true);
    try {
      const userIds = selectedMembers
        .filter(m => m.userId && m.userId !== userProfile?.id)
        .map(m => m.userId);

      if (userIds.length === 0) {
        toast.error('No valid members to remove');
        return;
      }

      const { error } = await supabase
        .from('company_members')
        .delete()
        .eq('company_id', company.id)
        .in('user_id', userIds);

      if (error) throw error;

      toast.success(`Successfully removed ${userIds.length} member${userIds.length !== 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error bulk removing members:', error);
      toast.error('Failed to remove some members');
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedMembers, company?.id, userProfile?.id, refetch]);

  const handleBulkSendInvite = useCallback(async () => {
    // Get members without accounts who don't have pending invitations
    const membersToInvite = selectedMembers.filter(m => !m.hasAccount && !m.isPending && m.userId);

    if (membersToInvite.length === 0) {
      toast.error('No members without accounts selected');
      return;
    }

    setBulkActionLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const member of membersToInvite) {
        try {
          const { data, error } = await supabase.functions.invoke('send-invitation', {
            body: {
              email: member.email,
              role: member.permissions || 'User',
              companyId: company.id,
              existingUserProfileId: member.userId
            }
          });

          if (error) {
            errorCount++;
            continue;
          }

          if (data?.error) {
            errorCount++;
            continue;
          }

          successCount++;
        } catch (err) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Sent ${successCount} invitation${successCount !== 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to send ${errorCount} invitation${errorCount !== 1 ? 's' : ''}`);
      }

      setSelectedIds(new Set());
      refetch();
    } catch (error) {
      console.error('Error bulk sending invites:', error);
      toast.error('Failed to send invitations');
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedMembers, company?.id, refetch]);

  // Show loading state while auth or permissions are loading
  if (authLoading || !permissionsLoaded) {
    return (
      <AppShell3>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          <div className='flex items-center justify-center h-64'>
            <LoadingSpinner />
          </div>
        </div>
      </AppShell3>
    );
  }

  // Show access denied if user doesn't have admin permissions
  if (!canManageUsers) {
    return (
      <AppShell3>
        <AccessDenied
          message="You don't have permission to access company user management. This page is only available to company administrators."
          backTo='/dashboard'
          backLabel='Back to Dashboard'
        />
      </AppShell3>
    );
  }

  const totalPages = pagination.totalPages || Math.ceil((pagination.totalCount || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, pagination.totalCount || 0);

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName="Company"
          toolName="Users"
          currentTool="users"
          spaceTools={globalTools}
          isGlobalView={true}
          actions={
            <div className='flex items-center gap-3'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                <Input
                  placeholder='Search users...'
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className='pl-10 bg-white w-64'
                />
              </div>

              {/* Space Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' size='sm' className='min-w-[120px]'>
                    <Filter className='h-4 w-4 mr-2' />
                    {filterSpace === 'all' ? 'All Spaces' : filterSpace}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='start'>
                  <DropdownMenuItem onClick={() => handleFilterSpaceChange('all')}>
                    All Spaces
                  </DropdownMenuItem>
                  {uniqueSpaceNames.map(space => (
                    <DropdownMenuItem
                      key={space}
                      onClick={() => handleFilterSpaceChange(space)}
                    >
                      {space}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Account Status Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' size='sm' className='min-w-[140px]'>
                    {filterAccountStatus === 'all' ? (
                      <>
                        <Users className='h-4 w-4 mr-2' />
                        All Users
                      </>
                    ) : filterAccountStatus === 'with_account' ? (
                      <>
                        <UserCheck className='h-4 w-4 mr-2' />
                        With Account
                      </>
                    ) : (
                      <>
                        <UserX className='h-4 w-4 mr-2' />
                        Without Account
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='start'>
                  <DropdownMenuItem onClick={() => handleFilterAccountStatusChange('all')}>
                    <Users className='h-4 w-4 mr-2' />
                    All Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFilterAccountStatusChange('with_account')}>
                    <UserCheck className='h-4 w-4 mr-2' />
                    With Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFilterAccountStatusChange('without_account')}>
                    <UserX className='h-4 w-4 mr-2' />
                    Without Account
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Archive Status Filter */}
              {stats.archived > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant={filterArchiveStatus !== 'active' ? 'default' : 'outline'} size='sm' className='min-w-[120px]'>
                      <Archive className='h-4 w-4 mr-2' />
                      {filterArchiveStatus === 'active' ? 'Active' : filterArchiveStatus === 'archived' ? 'Archived' : 'All'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='start'>
                    <DropdownMenuItem onClick={() => handleFilterArchiveStatusChange('active')}>
                      <Users className='h-4 w-4 mr-2' />
                      Active Users
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleFilterArchiveStatusChange('archived')}>
                      <Archive className='h-4 w-4 mr-2' />
                      Archived Users
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleFilterArchiveStatusChange('all')}>
                      <Users className='h-4 w-4 mr-2' />
                      All Users
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <Button variant='outline' size='sm' onClick={handleExportMembers}>
                <Download className='h-4 w-4 mr-2' />
                Export
              </Button>
              <Button size='sm' onClick={handleOpenInviteDialog}>
                <UserPlus className='h-4 w-4 mr-2' />
                Invite Member
              </Button>
            </div>
          }
        />

        {/* Main Content Container */}
        <div className='max-w-7xl mx-auto mt-4'>
          {/* Stats Cards */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Total Users</p>
                  <p className='text-2xl font-bold'>{stats.total}</p>
                </div>
                <Users className='h-8 w-8 text-blue-500 opacity-50' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Active Users</p>
                  <p className='text-2xl font-bold text-green-600'>{stats.active}</p>
                </div>
                <Users className='h-8 w-8 text-green-500 opacity-50' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Spaces</p>
                  <p className='text-2xl font-bold text-purple-600'>{stats.spaces}</p>
                </div>
                <Building className='h-8 w-8 text-purple-500 opacity-50' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Admins</p>
                  <p className='text-2xl font-bold text-red-600'>{stats.admins}</p>
                </div>
                <Shield className='h-8 w-8 text-red-500 opacity-50' />
              </div>
            </div>
          </div>

          {/* Users Table */}
          <TableCard.Root>
            <TableCard.Header
              title="Team members"
              badge={`${pagination.totalCount || 0} users`}
            />
            {/* Bulk Actions Bar */}
            {selectedCount > 0 && (
              <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">
                    {selectedCount} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    className="h-7 px-2"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Clear
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  {/* Only show Send Invite if there are members without accounts */}
                  {selectedMembers.some(m => !m.hasAccount && !m.isPending && !m.isArchived) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBulkSendInvite}
                      disabled={bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send Invites
                    </Button>
                  )}
                  {/* Archive button for active members */}
                  {selectedMembers.some(m => !m.isArchived && !m.isPending && m.userId !== userProfile?.id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkArchiveDialogOpen(true)}
                      disabled={bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Archive className="h-4 w-4 mr-2" />
                      )}
                      Archive
                    </Button>
                  )}
                  {/* Restore button for archived members */}
                  {selectedMembers.some(m => m.isArchived) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBulkRestore}
                      disabled={bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ArchiveRestore className="h-4 w-4 mr-2" />
                      )}
                      Restore
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDeleteDialogOpen(true)}
                    disabled={bulkActionLoading}
                  >
                    {bulkActionLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Remove from Company
                  </Button>
                </div>
              </div>
            )}

            <Table
              aria-label="Company team members"
              selectionMode="multiple"
              selectionBehavior="toggle"
              selectedKeys={selectedIds}
              onSelectionChange={(keys) => {
                // React Aria returns 'all' or a Set
                if (keys === 'all') {
                  const selectableIds = members
                    .filter(m => !m.isPending && m.userId)
                    .map(m => m.id);
                  setSelectedIds(new Set(selectableIds));
                } else {
                  setSelectedIds(new Set(keys));
                }
              }}
              disabledKeys={members.filter(m => m.isPending || !m.userId).map(m => m.id)}
            >
              <Table.Header>
                <Table.Head id="member" label="Member" isRowHeader className="w-[24%]" />
                <Table.Head id="role" label="Job Title" className="w-[16%]" />
                <Table.Head id="spaces" label="Spaces" className="w-[20%]" />
                <Table.Head id="contact" label="Contact" className="w-[18%]" />
                <Table.Head id="access" label="Access" className="w-[12%]" />
                <Table.Head id="actions" label="" className="w-[10%]" />
              </Table.Header>

              <Table.Body items={loading ? skeletonItems : members}>
                {(member) => (
                  <Table.Row id={member.id}>
                    <Table.Cell>
                      {member.isLoading ? (
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <div className="space-y-1.5">
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-3 w-36" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarImage src={member.avatar} alt={member.name} />
                            <AvatarFallback className="text-xs">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-gray-900 truncate" title={member.name}>
                                {member.name}
                              </p>
                              {member.isPending && (
                                <Badge variant='outline' className='text-[9px] px-1 py-0 text-amber-600 border-amber-300'>
                                  Pending
                                </Badge>
                              )}
                              {member.isArchived && (
                                <Badge variant='outline' className='text-[9px] px-1 py-0 text-gray-500 border-gray-300 bg-gray-50'>
                                  Archived
                                </Badge>
                              )}
                              {member.status === 'invited' && !member.isPending && !member.isArchived && (
                                <Badge variant='secondary' className='text-[9px] px-1 py-0'>
                                  No Account
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate" title={member.email}>
                              {member.email}
                            </p>
                          </div>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {member.isLoading ? (
                        <div className="space-y-1.5">
                          <Skeleton className="h-4 w-20" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      ) : (
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate" title={member.role}>
                            {member.role || '-'}
                          </p>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {member.isLoading ? (
                        <div className="flex gap-1">
                          <Skeleton className="h-5 w-14 rounded-full" />
                          <Skeleton className="h-5 w-14 rounded-full" />
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {member.spaces?.slice(0, 2).map((space, idx) => (
                            <Badge
                              key={idx}
                              variant='outline'
                              className='text-[10px] px-1.5 py-0'
                            >
                              {space.name}
                            </Badge>
                          ))}
                          {member.spaces?.length > 2 && (
                            <Badge variant='secondary' className='text-[10px] px-1.5 py-0'>
                              +{member.spaces.length - 2}
                            </Badge>
                          )}
                          {(!member.spaces || member.spaces.length === 0) && (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {member.isLoading ? (
                        <div className="space-y-1.5">
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      ) : (
                        <div className="text-sm min-w-0">
                          <div className="flex items-center gap-1 text-gray-600">
                            <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="truncate text-xs" title={member.location}>
                              {member.location || '-'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-0.5 text-gray-600">
                            <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="truncate text-xs" title={member.phone}>
                              {member.phone || '-'}
                            </span>
                          </div>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {member.isLoading ? (
                        <Skeleton className="h-5 w-14 rounded-full" />
                      ) : (
                        <Badge variant={getPermissionBadgeColor(member.permissions)}>
                          {member.permissions || 'User'}
                        </Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {member.isLoading ? (
                        <div className="flex justify-end">
                          <Skeleton className="h-8 w-8 rounded" />
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-8 w-8 p-0'
                                onClick={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className='h-4 w-4' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              {member.userId && (
                                <DropdownMenuItem onClick={() => handleViewProfile(member.userId)}>
                                  <Eye className='h-4 w-4 mr-2' />
                                  View Profile
                                </DropdownMenuItem>
                              )}
                              {!member.isPending && !member.isArchived && (
                                <DropdownMenuItem onClick={() => handleEditMember(member)}>
                                  <Edit className='h-4 w-4 mr-2' />
                                  Edit Member
                                </DropdownMenuItem>
                              )}
                              {!member.hasAccount && !member.isPending && !member.isArchived && (
                                <DropdownMenuItem onClick={() => handleSendInvitation(member)}>
                                  <Send className='h-4 w-4 mr-2' />
                                  Send Invitation
                                </DropdownMenuItem>
                              )}
                              {member.isPending && (
                                <DropdownMenuItem onClick={() => handleResendInvitation(member)}>
                                  <RefreshCw className='h-4 w-4 mr-2' />
                                  Resend Invitation
                                  {member.resentCount > 0 && (
                                    <span className='ml-1 text-xs text-muted-foreground'>
                                      ({member.resentCount})
                                    </span>
                                  )}
                                </DropdownMenuItem>
                              )}
                              {member.isPending && (
                                <DropdownMenuItem
                                  onClick={() => handleCancelInvitation(member)}
                                  className='text-red-600 focus:text-red-600'
                                >
                                  <XCircle className='h-4 w-4 mr-2' />
                                  Cancel Invitation
                                </DropdownMenuItem>
                              )}
                              {!member.isPending && !member.isArchived && member.userId !== userProfile?.id && (
                                <DropdownMenuItem onClick={() => handleArchiveMember(member)}>
                                  <Archive className='h-4 w-4 mr-2' />
                                  Archive User
                                </DropdownMenuItem>
                              )}
                              {member.isArchived && (
                                <DropdownMenuItem onClick={() => handleRestoreMember(member)}>
                                  <ArchiveRestore className='h-4 w-4 mr-2' />
                                  Restore User
                                </DropdownMenuItem>
                              )}
                              {!member.isPending && member.userId !== userProfile?.id && (
                                <DropdownMenuItem
                                  onClick={() => handleDeleteMember(member)}
                                  className='text-red-600 focus:text-red-600'
                                >
                                  <Trash2 className='h-4 w-4 mr-2' />
                                  Remove from Company
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {/* Pagination */}
            <div className="px-4 py-3 md:px-6 md:pt-3 md:pb-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700">
                  Showing <span className="font-medium">{pagination.totalCount > 0 ? startIndex + 1 : 0}</span> to{' '}
                  <span className="font-medium">{endIndex}</span> of{' '}
                  <span className="font-medium">{pagination.totalCount || 0}</span> results
                </span>

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700">Per page:</span>
                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={handleItemsPerPageChange}
                    >
                      <SelectTrigger className="w-20 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          return (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          );
                        })
                        .map((page, index, array) => {
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;

                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && (
                                <span className="px-2 text-gray-500">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className="w-9"
                              >
                                {page}
                              </Button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TableCard.Root>
        </div>

        {/* Invite Team Member Dialog - Lazy loaded */}
        {inviteDialogOpen && (
          <Suspense fallback={null}>
            <InviteTeamMemberDialog
              open={inviteDialogOpen}
              onOpenChange={setInviteDialogOpen}
              onMemberAdded={refetch}
            />
          </Suspense>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to remove <strong>{memberToDelete?.name}</strong> from {company?.name}?
                </AlertDialogDescription>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  This will remove them from the company and all associated spaces. They will no longer have access to company data.
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDeleteMember}
                  className='bg-red-600 hover:bg-red-700 focus:ring-red-600'
                >
                  Remove Member
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Archive Confirmation Dialog */}
        <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Archive User</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to archive <strong>{memberToArchive?.name}</strong>?
                </AlertDialogDescription>
              </div>
              <div className="rounded-lg bg-muted p-4 space-y-2">
                {memberToArchive?.spaces?.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    They will be removed from <strong>{memberToArchive.spaces.length} space{memberToArchive.spaces.length !== 1 ? 's' : ''}</strong>.
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  Their qualifications and documents will be preserved. You can restore them at any time.
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel disabled={archiveLoading}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmArchiveMember}
                  disabled={archiveLoading}
                >
                  {archiveLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4 mr-2" />
                      Archive User
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Restore Confirmation Dialog */}
        <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Restore User</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to restore <strong>{memberToRestore?.name}</strong>?
                </AlertDialogDescription>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  They will become an available company member but will not be assigned to any spaces. You can add them to spaces afterwards.
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel disabled={restoreLoading}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmRestoreMember}
                  disabled={restoreLoading}
                >
                  {restoreLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <ArchiveRestore className="h-4 w-4 mr-2" />
                      Restore User
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Archive Confirmation Dialog */}
        <AlertDialog open={bulkArchiveDialogOpen} onOpenChange={setBulkArchiveDialogOpen}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Archive {selectedMembers.filter(m => !m.isArchived && !m.isPending && m.userId !== userProfile?.id).length} User{selectedMembers.filter(m => !m.isArchived && !m.isPending && m.userId !== userProfile?.id).length !== 1 ? 's' : ''}</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to archive the selected users? They will be removed from all spaces.
                </AlertDialogDescription>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  Their qualifications and documents will be preserved. You can restore them at any time.
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleBulkArchive}
                  disabled={bulkActionLoading}
                >
                  {bulkActionLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4 mr-2" />
                      Archive Users
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div>
                <AlertDialogTitle>Remove {selectedMembers.length} Team Members</AlertDialogTitle>
                <AlertDialogDescription className="mt-2">
                  Are you sure you want to remove <strong>{selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''}</strong> from {company?.name}?
                </AlertDialogDescription>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  This will remove them from the company and all associated spaces. They will no longer have access to company data.
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleBulkRemove}
                  className='bg-red-600 hover:bg-red-700 focus:ring-red-600'
                  disabled={bulkActionLoading}
                >
                  {bulkActionLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    `Remove ${selectedMembers.length} Member${selectedMembers.length !== 1 ? 's' : ''}`
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </PageTransition>
    </AppShell3>
  );
}

export default CompanyTeamPage;
